/**
 * JAISIU-4447 — pending-seek bookkeeping for the custom media bars.
 *
 * ## Why this exists
 *
 * The custom progress bar is the ONLY scrubber in the overlay now
 * that the native `<audio controls>` / `<video controls>` strips are
 * gone (JAISIU-4447). That makes the bar the source of truth for the
 * playhead position, and it means the bar must never show a position
 * the operator did NOT ask for.
 *
 * ## The problem
 *
 * Seeking a media element is ASYNCHRONOUS:
 *
 *   1. the operator drags the pointer to 60% of the clip;
 *   2. the bar asks the media element to jump there (`currentTime = t`);
 *   3. the browser starts fetching the new range and, until it lands,
 *      keeps reporting the OLD `currentTime`;
 *   4. meanwhile the element fires `timeupdate` (and later `seeked`).
 *
 * If the bar simply renders whatever `currentTime` says, steps 3-4
 * snap the playhead back to the old position and then jump forward —
 * the operator's drag visibly "slips back". With the native chrome
 * gone there is no second scrubber to correct it, so the bar has to
 * hold the requested position itself.
 *
 * ## The contract
 *
 * The tracker holds ONE pending seek at a time:
 *
 *   - `requestSeek(t)` records the target the operator asked for.
 *   - `resolveSeek(currentTime)` returns the position the bar should
 *     render. While a seek is pending AND the media still reports a
 *     materially different position, the PENDING target wins — stale
 *     `timeupdate` / `seeked` events cannot overwrite it.
 *   - Once the media reports a position within {@link SEEK_TOLERANCE_SECONDS}
 *     of the target, the seek is considered COMPLETE: the pending
 *     state is cleared and the media position takes over again.
 *
 * A later seek REPLACES the pending target (drag gestures seek
 * continuously), and the tolerance is expressed in seconds so it
 * behaves the same on a 4-second clip and a 4-minute one.
 */

/**
 * How far the media element's reported position may be from the
 * pending seek target before the seek is considered "not landed
 * yet" and the pending target keeps winning.
 *
 * One second is a good compromise: it is smaller than any meaningful
 * gesture (the smallest keyboard nudge is 5s), but large enough to
 * absorb the browser's own rounding when it lands a seek on a
 * keyframe boundary.
 */
export const SEEK_TOLERANCE_SECONDS = 1;

/**
 * State of a single media element's seek bookkeeping.
 */
export interface PendingSeekState {
  /** The target time the operator asked for, in seconds. */
  target: number;
  /** Whether the target was already consumed by a completed seek. */
  settled: boolean;
}

/**
 * Create an empty tracker state.
 */
export function createSeekState(): PendingSeekState {
  return { target: 0, settled: true };
}

/**
 * Record a seek request.
 *
 * Returns the new state. A request replaces any previous pending
 * target — a drag produces a stream of requests and only the last
 * one matters.
 */
export function requestSeek(
  state: PendingSeekState,
  seconds: number,
): PendingSeekState {
  if (!Number.isFinite(seconds) || seconds < 0) return state;
  return { target: seconds, settled: false };
}

/**
 * Decide which position the bar should render, given the media
 * element's reported `currentTime`.
 *
 * Returns the time to render. When a seek is pending and the media
 * has not caught up yet, the pending TARGET is returned so stale
 * `timeupdate` / `seeked` events cannot move the playhead back.
 * Once the media lands within {@link SEEK_TOLERANCE_SECONDS} of the
 * target, the seek is settled and the media position is authoritative
 * again.
 */
export function resolveSeek(
  state: PendingSeekState,
  currentTime: number,
  tolerance = SEEK_TOLERANCE_SECONDS,
): number {
  if (state.settled) {
    return Number.isFinite(currentTime) ? currentTime : 0;
  }
  const mediaTime = Number.isFinite(currentTime) ? currentTime : 0;
  // The seek has landed when the media is within tolerance of the
  // requested target (either side — the browser may snap slightly
  // backwards onto the nearest keyframe).
  if (Math.abs(mediaTime - state.target) <= tolerance) {
    return state.target;
  }
  // Still in flight: hold the operator's requested position.
  return state.target;
}

/**
 * Update the tracker against the media's reported position.
 *
 * Returns the NEW state plus the position the bar should render.
 *
 * The state is settled when:
 *
 *   - there was no pending seek (nothing to settle), or
 *   - the media position landed within the tolerance of the target.
 */
export function reconcileSeek(
  state: PendingSeekState,
  currentTime: number,
  tolerance = SEEK_TOLERANCE_SECONDS,
): { state: PendingSeekState; time: number } {
  if (state.settled) {
    return { state, time: Number.isFinite(currentTime) ? currentTime : 0 };
  }
  const mediaTime = Number.isFinite(currentTime) ? currentTime : 0;
  if (Math.abs(mediaTime - state.target) <= tolerance) {
    // The seek completed — the media owns the position again.
    return { state: { target: state.target, settled: true }, time: state.target };
  }
  // The seek has NOT completed: the media is still reporting a stale
  // position, so the requested target keeps winning.
  return { state, time: state.target };
}
